import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNexusStore } from '@/stores/nexusStore';
import { Hexagon, Check, ChevronRight, ChevronLeft, Users, Plug, Server, Bell, Clock, Zap, Sparkles, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEPS = [
  { id: 1, title: 'Welcome & Org Setup', icon: Hexagon, desc: 'Set up your organization' },
  { id: 2, title: 'Invite Your Team', icon: Users, desc: 'Add team members' },
  { id: 3, title: 'Connect Monitoring', icon: Plug, desc: 'Link your monitoring tools' },
  { id: 4, title: 'Import CIs', icon: Server, desc: 'Import configuration items' },
  { id: 5, title: 'Configure Notifications', icon: Bell, desc: 'Set up alert channels' },
  { id: 6, title: 'Set SLA Targets', icon: Clock, desc: 'Define response times' },
  { id: 7, title: 'Live Demo', icon: Zap, desc: 'Watch ARIA resolve an incident' },
  { id: 8, title: "You're Live!", icon: Sparkles, desc: 'Start using NEXUS' },
];

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [orgName, setOrgName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [emails, setEmails] = useState('');
  const [monitoringTool, setMonitoringTool] = useState('');
  const [slaTimes, setSlaTimes] = useState({ P1: '4', P2: '8', P3: '24', P4: '48' });
  const [demoTriggered, setDemoTriggered] = useState(false);
  const [demoTicket, setDemoTicket] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');

  // Fetch existing onboarding state
  const { data: onboardingState } = useQuery({
    queryKey: ['onboarding'],
    queryFn: async () => {
      const { data } = await axios.get(`${BACKEND_URL}/api/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
  });

  useEffect(() => {
    if (onboardingState) {
      const s = onboardingState.current_step;
      if (typeof s === 'number' && s >= 1 && s <= 8) setStep(s);
      if (Array.isArray(onboardingState.completed_steps)) setCompletedSteps(new Set(onboardingState.completed_steps.filter(x => typeof x === 'number')));
      if (onboardingState.org_name) setOrgName(onboardingState.org_name);
    }
  }, [onboardingState]);

  const saveMutation = useMutation({
    mutationFn: async ({ stepNum, data }) => {
      const { data: result } = await axios.patch(`${BACKEND_URL}/api/onboarding/step`, 
        { step: stepNum, data }, { headers: { Authorization: `Bearer ${token}` } });
      return result;
    },
  });

  const completeStep = (stepNum) => {
    setCompletedSteps(prev => new Set([...prev, stepNum]));
    saveMutation.mutate({ stepNum, data: { [`step_${stepNum}_completed`]: true } });
  };

  const nextStep = () => {
    completeStep(step);
    if (step < 8) setStep(step + 1);
  };

  const triggerDemo = async () => {
    setDemoTriggered(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/dev/trigger-scenario`, 
        { scenario_id: 'SCENARIO_MEMORY_LEAK' }, { headers: { Authorization: `Bearer ${token}` } });
      setDemoTicket(data.ticket_id);
      toast.success(`Demo incident ${data.ticket_id} created!`, { description: 'ARIA is resolving it now...' });
    } catch {
      toast.error('Demo trigger failed');
      setDemoTriggered(false);
    }
  };

  const finishOnboarding = () => {
    completeStep(8);
    saveMutation.mutate({ stepNum: 8, data: { onboarding_complete: true } });
    toast.success('Onboarding complete! Welcome to NEXUS ITSM');
    navigate('/');
  };

  return (
    <div data-testid="onboarding-page" className="w-full max-w-[900px] mx-auto py-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--nexus-accent)] bg-opacity-15 flex items-center justify-center">
            <Hexagon size={20} className="text-[var(--nexus-accent)]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[var(--nexus-text-primary)]">NEXUS ITSM Setup</h1>
        </div>
        <p className="text-sm text-[var(--nexus-text-muted)]">Get your organization up and running in under 30 minutes</p>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-1 mb-8 px-4">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = s.id === step;
          const isDone = completedSteps.has(s.id);
          return (
            <div key={s.id} className="flex items-center flex-1">
              <button onClick={() => setStep(s.id)} data-testid={`step-${s.id}`}
                className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-mono transition-all ${
                  isDone ? 'bg-[var(--nexus-success)] text-white' :
                  isActive ? 'bg-[var(--nexus-accent)] text-white' :
                  'bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)]'
                }`}>
                {isDone ? <Check size={14} /> : s.id}
              </button>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 rounded ${isDone ? 'bg-[var(--nexus-success)]' : 'bg-[var(--nexus-border)]'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="nexus-card relative overflow-hidden animate-nexus-slide-in" key={step}>
        <div className="p-4 border-b border-[var(--nexus-border)]">
          <div className="flex items-center gap-2">
            <Hexagon size={18} className="text-[var(--nexus-accent)]" />
            <h2 className="text-lg font-semibold text-[var(--nexus-text-primary)]">Step {step}: {STEPS[step-1]?.title}</h2>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Org Setup */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Let's set up your organization in NEXUS.</p>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Organization Name</label>
                <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} data-testid="org-name"
                  placeholder="Acme Corporation" className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Timezone</label>
                <select value={timezone} onChange={e => setTimezone(e.target.value)} data-testid="timezone"
                  className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-secondary)] outline-none">
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern (US)</option>
                  <option value="America/Chicago">Central (US)</option>
                  <option value="America/Los_Angeles">Pacific (US)</option>
                  <option value="Europe/London">London (UK)</option>
                  <option value="Europe/Berlin">Central Europe</option>
                  <option value="Asia/Tokyo">Tokyo (Japan)</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Invite Team */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Invite your IT team members. You can add more later from Settings.</p>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Email Addresses (one per line)</label>
                <textarea value={emails} onChange={e => setEmails(e.target.value)} rows={4} data-testid="invite-emails"
                  placeholder={"ops-lead@acme.com\nsre-team@acme.com\nhelp-desk@acme.com"}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] resize-none" />
              </div>
              <p className="text-[10px] text-[var(--nexus-text-muted)]">Roles can be assigned after invitation. Default role: IT Analyst.</p>
            </div>
          )}

          {/* Step 3: Connect Monitoring */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Connect your monitoring tools so NEXUS can receive alerts.</p>
              <div className="grid grid-cols-2 gap-3">
                {['Datadog', 'PagerDuty', 'CloudWatch', 'Prometheus'].map(tool => (
                  <button key={tool} onClick={() => setMonitoringTool(tool)} data-testid={`connect-${tool.toLowerCase()}`}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      monitoringTool === tool ? 'border-[var(--nexus-accent)] bg-[var(--nexus-accent)]/10' : 'border-[var(--nexus-border)] bg-[var(--nexus-bg-base)] hover:bg-white/[0.03]'
                    }`}>
                    <span className="text-sm font-semibold text-[var(--nexus-text-primary)]">{tool}</span>
                    <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">Webhook: /api/webhooks/{tool.toLowerCase()}</p>
                  </button>
                ))}
              </div>
              {monitoringTool && (
                <div className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <p className="text-[10px] uppercase tracking-wider text-[var(--nexus-accent)] font-medium mb-1">Webhook URL</p>
                  <code className="text-xs font-mono text-[var(--nexus-text-secondary)] break-all">
                    {BACKEND_URL}/api/webhooks/{monitoringTool.toLowerCase()}
                  </code>
                  <p className="text-[10px] text-[var(--nexus-text-muted)] mt-2">Add this URL in your {monitoringTool} webhook settings. NEXUS will auto-route alerts through MOA → ARIA.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Import CIs */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Import your configuration items into the CMDB. ATLAS will auto-discover cloud resources.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <span className="text-sm font-semibold text-[var(--nexus-text-primary)]">Auto-Discovery</span>
                  <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">ATLAS scans AWS, Azure, Kubernetes for CIs automatically</p>
                  <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded bg-[var(--nexus-success)] bg-opacity-15 text-[var(--nexus-success)]">Recommended</span>
                </div>
                <div className="p-4 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                  <span className="text-sm font-semibold text-[var(--nexus-text-primary)]">CSV Import</span>
                  <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">Upload a CSV file with your CI inventory</p>
                </div>
              </div>
              <p className="text-[10px] text-[var(--nexus-text-muted)]">You already have {onboardingState?.ci_count || 12} CIs from seed data. Skip this step if using demo data.</p>
            </div>
          )}

          {/* Step 5: Notifications */}
          {step === 5 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Configure how HERALD sends notifications to your team.</p>
              <div className="space-y-3">
                {[{name: 'Slack', desc: 'Connect a Slack workspace for real-time alerts'}, {name: 'Microsoft Teams', desc: 'Connect Teams for incident notifications'}, {name: 'Email', desc: 'Configure email distribution lists'}].map(ch => (
                  <div key={ch.name} className="flex items-center justify-between p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                    <div><span className="text-sm text-[var(--nexus-text-primary)]">{ch.name}</span><p className="text-[10px] text-[var(--nexus-text-muted)]">{ch.desc}</p></div>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)]">Configure</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 6: SLA Targets */}
          {step === 6 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Set your SLA resolution targets. These drive the SLA breach prediction engine.</p>
              <div className="space-y-2">
                {Object.entries(slaTimes).map(([priority, hours]) => (
                  <div key={priority} className="flex items-center gap-3">
                    <span className={`font-mono text-[10px] px-2 py-1 rounded w-12 text-center badge-${priority.toLowerCase()}`}>{priority}</span>
                    <input type="number" value={hours} onChange={e => setSlaTimes(prev => ({...prev, [priority]: e.target.value}))}
                      className="w-20 px-2 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] outline-none text-center font-mono" />
                    <span className="text-xs text-[var(--nexus-text-muted)]">hours to resolve</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 7: Live Demo */}
          {step === 7 && (
            <div className="space-y-4 text-center">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Watch ARIA autonomously resolve a real incident. This is the defining experience of NEXUS.</p>
              {!demoTriggered ? (
                <button onClick={triggerDemo} data-testid="trigger-demo"
                  className="px-6 py-3 rounded-xl bg-[var(--nexus-accent)] text-white text-sm font-semibold hover:bg-opacity-90 transition-colors">
                  <Zap size={16} className="inline mr-2" /> Fire a Demo Incident
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="p-4 rounded-lg bg-[var(--nexus-accent)] bg-opacity-10 border border-cyan-500/20">
                    <p className="text-sm text-[var(--nexus-accent)] font-semibold">ARIA is working on {demoTicket || 'the incident'}...</p>
                    <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">Watch the 9-node resolution: ingest → enrich → correlate → classify → RCA → plan → execute → validate → PIR</p>
                  </div>
                  {demoTicket && (
                    <button onClick={() => navigate(`/incidents/${demoTicket}`)} data-testid="view-demo-ticket"
                      className="px-4 py-2 rounded-lg bg-[var(--nexus-primary-bright)] text-white text-xs font-medium hover:bg-opacity-90">
                      View Live Feed: {demoTicket}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 8: Complete */}
          {step === 8 && (
            <div className="text-center space-y-4">
              <div className="text-4xl mb-2">🎉</div>
              <h3 className="font-heading text-xl font-bold text-[var(--nexus-text-primary)]">You're All Set!</h3>
              <p className="text-sm text-[var(--nexus-text-secondary)]">NEXUS ITSM is ready. Your 11 AI agents are standing by.</p>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <button onClick={() => navigate('/')} className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.04] transition-colors">
                  <span className="text-xs font-semibold text-[var(--nexus-text-primary)]">Command Center</span>
                  <p className="text-[10px] text-[var(--nexus-text-muted)]">Live dashboard</p>
                </button>
                <button onClick={() => navigate('/analytics')} className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.04] transition-colors">
                  <span className="text-xs font-semibold text-[var(--nexus-text-primary)]">Analytics</span>
                  <p className="text-[10px] text-[var(--nexus-text-muted)]">KPIs & metrics</p>
                </button>
                <button onClick={() => navigate('/docs')} className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.04] transition-colors">
                  <span className="text-xs font-semibold text-[var(--nexus-text-primary)]">Documentation</span>
                  <p className="text-[10px] text-[var(--nexus-text-muted)]">Learn more</p>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="p-4 border-t border-[var(--nexus-border)] flex items-center justify-between">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] disabled:opacity-30">
            <ChevronLeft size={14} /> Back
          </button>
          <span className="text-[10px] text-[var(--nexus-text-muted)] font-mono">Step {step} of 8</span>
          {step < 8 ? (
            <button onClick={nextStep} data-testid="next-step"
              className="flex items-center gap-1 px-4 py-1.5 rounded-lg bg-[var(--nexus-accent)] text-white text-xs font-medium hover:bg-opacity-90">
              {step === 7 ? 'Finish Setup' : 'Next'} <ChevronRight size={14} />
            </button>
          ) : (
            <button onClick={finishOnboarding} data-testid="go-live"
              className="flex items-center gap-1 px-4 py-1.5 rounded-lg bg-[var(--nexus-success)] text-white text-xs font-medium hover:bg-opacity-90">
              Go to Command Center <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
